import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import {
  PoliticaPrivacidad,
  RegistroAsentamiento,
  RegistroMedicoRequest,
  RegistroMedicoResponse,
  RegistroPacienteRequest,
  ColegiadoValidationResponse,
  SolicitudRegistro,
  SolicitudRegistroFilters,
  SolicitudRegistroStatus,
} from '@core/models/Registration';
import { FederalEntity } from '@core/models/FederalEntity';
import { GeneroVisible } from '@core/models/GeneroVisible';
import { environment } from '@enviroment/environment';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  private readonly baseUrl = `${environment.backend_url}Registro`;
  private readonly colegiadosBaseUrl = `${environment.backend_url}Colegiados`;

  constructor(private http: HttpClient) {}

  registerPatient(payload: RegistroPacienteRequest) {
    return this.http.post<DefaultResponse<string>>(
      `${this.baseUrl}/paciente`,
      payload
    );
  }

  registerDoctor(payload: RegistroMedicoRequest) {
    return this.http.post<DefaultResponse<RegistroMedicoResponse>>(
      `${this.baseUrl}/medico`,
      payload
    );
  }

  approveDoctorRequest(idSolicitudRegistro: number) {
    return this.http.post<DefaultResponse<RegistroMedicoResponse>>(
      `${this.baseUrl}/aprobar/${idSolicitudRegistro}`,
      {}
    );
  }

  rejectRequest(idSolicitudRegistro: number, motivoRechazo: string) {
    return this.http.post<DefaultResponse<RegistroMedicoResponse>>(
      `${this.baseUrl}/rechazar/${idSolicitudRegistro}`,
      { motivoRechazo }
    );
  }

  getRequestStatusByFolio(folio: string) {
    return this.http.get<DefaultResponse<SolicitudRegistroStatus>>(
      `${this.baseUrl}/estatus/${encodeURIComponent(folio)}`
    );
  }

  validateColegiado(tipoColegiado: number, numeroColegiado: string) {
    const endpointMap: Record<number, string> = {
      1: 'medicos',
      2: 'dentistas',
      3: 'podologos',
    };

    const endpoint = endpointMap[tipoColegiado];
    if (!endpoint) {
      throw new Error(`TIPO DE COLEGIADO NO SOPORTADO: ${tipoColegiado}`);
    }

    return this.http.get<ColegiadoValidationResponse>(
      `${this.colegiadosBaseUrl}/${endpoint}/${encodeURIComponent(numeroColegiado)}`
    );
  }

  getRequests(filters?: SolicitudRegistroFilters) {
    let params = new HttpParams();

    if (filters?.estatus) {
      params = params.set('estatus', filters.estatus);
    }

    if (filters?.tipoSolicitud) {
      params = params.set('tipoSolicitud', filters.tipoSolicitud);
    }

    if (filters?.folio) {
      params = params.set('folio', filters.folio);
    }

    if (filters?.email) {
      params = params.set('email', filters.email);
    }

    return this.http.get<DefaultResponse<SolicitudRegistro[]>>(
      `${this.baseUrl}/solicitudes`,
      { params }
    );
  }

  getSettlementsByPostalCode(codigoPostal: string) {
    const params = new HttpParams().set('codigoPostal', codigoPostal);

    return this.http.get<DefaultResponse<RegistroAsentamiento[]>>(
      `${this.baseUrl}/asentamientos-by-cp`,
      { params }
    );
  }

  getFederalEntities() {
    return this.http.get<DefaultResponse<FederalEntity[]>>(
      `${this.baseUrl}/entidades-federativas`
    );
  }

  getVisibleGenders() {
    return this.http.get<DefaultResponse<GeneroVisible[]>>(
      `${this.baseUrl}/generos-visibles`
    );
  }

  getPrivacyPolicies() {
    return this.http.get<DefaultResponse<any[]>>(
      `${environment.backend_url}PoliticasPrivacidad`
    ).pipe(
      map((response) => ({
        ...response,
        data: (response.data ?? []).map((item: any) => ({
          idPoliticaPrivacidad: Number(item.idPoliticaPrivacidad ?? item.IdPoliticaPrivacidad ?? 0),
          titulo: String(item.titulo ?? item.Titulo ?? ''),
          tipo: String(item.tipo ?? item.Tipo ?? ''),
          edicion: Number(item.edicion ?? item.Edicion ?? 0),
          contenido: String(item.contenido ?? item.Contenido ?? ''),
          requiereAceptacion: item.requiereAceptacion ?? item.RequiereAceptacion ?? false,
          activo: item.activo ?? item.Activo ?? false,
          fechaCreacion: item.fechaCreacion ?? item.FechaCreacion ?? null,
        })) as PoliticaPrivacidad[],
      }))
    );
  }
}
